import type { GetExternalLinkResponse } from '@vy/api-definition';
import { ENTITY_TYPES } from '@vy/integrations-types';
import { notDefined } from '@vy/null';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { useQuery, QueryClient } from 'react-query';
import { Nullable, purge, validateNotNullOrUndefined } from '@vy/utils';
import { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';

export type ExternalLinkProvider = 'Hubspot' | 'Salesforce';

export type ExternalLinkEntityType = 'contract' | 'customer';

type IntegrationEntity = { integrationProvider: string };

const CRM_PROVIDERS = new Set<string>([
  INTEGRATION_PROVIDERS.hubspot,
  INTEGRATION_PROVIDERS.salesforce,
]);

const EXTERNAL_LINK_LABELS: Record<ExternalLinkProvider, Record<ExternalLinkEntityType, string>> = {
  Hubspot: {
    [ENTITY_TYPES.CUSTOMER]: 'See HubSpot company',
    [ENTITY_TYPES.CONTRACT]: 'See HubSpot deal',
  },
  Salesforce: {
    [ENTITY_TYPES.CUSTOMER]: 'See Salesforce account',
    [ENTITY_TYPES.CONTRACT]: 'See Salesforce opportunity',
  },
};

export function getCrmProviderFromIntegrationEntities(
  entities: IntegrationEntity[] | null | undefined,
): ExternalLinkProvider | null {
  const crmEntity = entities?.find((entity) => CRM_PROVIDERS.has(entity.integrationProvider));

  if (notDefined(crmEntity)) {
    return null;
  }

  if (crmEntity.integrationProvider === INTEGRATION_PROVIDERS.hubspot) {
    return 'Hubspot';
  }

  if (crmEntity.integrationProvider === INTEGRATION_PROVIDERS.salesforce) {
    return 'Salesforce';
  }

  return null;
}

export function getExternalLinkLabel(
  provider: ExternalLinkProvider,
  entityType: ExternalLinkEntityType,
): string {
  return EXTERNAL_LINK_LABELS[provider][entityType];
}

async function externalLinkQuery(provider: ExternalLinkProvider, entityType: ExternalLinkEntityType, entityId: string) {
  const api = getPrivateApi();

  return api.getExternalLink({ linkProvider: provider, entityType, entityId });
}

export function useGetExternalLink(
  provider: Nullable<ExternalLinkProvider>,
  entityType: ExternalLinkEntityType,
  entityId: string,
  options?: CustomUseQueryOptions<GetExternalLinkResponse>,
) {
  const queryDeps = purge([provider, entityType, entityId]);

  return useQuery(
    queryKeys.externalLink(...queryDeps),
    () => {
      validateNotNullOrUndefined(provider, 'provider');

      return externalLinkQuery(provider, entityType, entityId);
    },
    {
      ...options,
    },
  );
}

export async function prefetchExternalLink(
  queryClient: QueryClient,
  provider: ExternalLinkProvider,
  entityType: ExternalLinkEntityType,
  entityId: string,
) {
  const queryDeps = purge([provider, entityType, entityId]);

  return queryClient.prefetchQuery(
    queryKeys.externalLink(...queryDeps),
    () => externalLinkQuery(provider, entityType, entityId),
  );
}
